import { Component } from "react";

import type { EntityValidator, FieldValidationResult, ValidatorResult } from "@bokio/shared/validation/entityValidator";
import type { FormEvent, ReactNode } from "react";

type ValidatorFunction<FormData, Result> = (
	formData: FormData,
	event: "change" | "submit",
	prevValidation?: Result & { isValid: boolean; errors?: FieldValidationResult[] },
	hasSubmittedOnce?: boolean,
) => Result & { isValid: boolean; errors?: FieldValidationResult[] };

export const asValidatorFunction =
	<FormData,>(oldStyleValidator: EntityValidator<FormData>): ValidatorFunction<FormData, ValidatorResult> =>
	(formData, event, _prevValidation, hasSubmittedOnce = false): ValidatorResult & { isValid: boolean } => {
		// After first submit attempt, continue validating
		const submitClicked = event === "submit" || hasSubmittedOnce;
		const result = oldStyleValidator.validate(formData, false, submitClicked);
		return { isValid: result.errors.length === 0, ...result };
	};

interface FormWithValidationProps<FormData, Result> {
	initialState: FormData;
	className?: string;
	validator: ValidatorFunction<FormData, Result>;
	children: (state: FormHandle<FormData, Result>) => ReactNode;
	onChange?: (formData: FormData, result: Result | undefined) => void;
	onSubmit: (formData: FormData, result: Result) => void;
	stopSubmitPropagation?: boolean;
}

interface FormWithValidationState<FormData, Result> {
	formData: FormData;
	hasSubmittedOnce: boolean;
	isPristine: boolean;
	validation: Result & { isValid: boolean; errors?: FieldValidationResult[] };
}

export interface FormHandle<FormData, Result> extends FormWithValidationState<FormData, Result> {
	setValue: (key: keyof FormData) => (val: unknown) => void;
	setFormData: (reducer: (oldFormData: FormData) => FormData) => void;
	clearSubmissionState: () => void;
}

export const DefaultFormValidator = () => ({ isValid: true });

export class FormWithValidation<FormData, Result> extends Component<
	FormWithValidationProps<FormData, Result>,
	FormWithValidationState<FormData, Result>
> {
	state: FormWithValidationState<FormData, Result> = {
		formData: this.props.initialState,
		hasSubmittedOnce: false,
		isPristine: true,
		validation: this.props.validator(this.props.initialState, "change", undefined, false),
	};

	render() {
		const parameters: FormHandle<FormData, Result> = {
			...this.state,

			setValue: this.setValue,
			setFormData: this.setFormData,
			clearSubmissionState: this.clearSubmissionState,
		};
		const content = this.props.children(parameters);
		return (
			<form onSubmit={this.onSubmit} className={this.props.className}>
				{content}
			</form>
		);
	}

	setValue = (key: string | number | symbol) => (newValue: unknown) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.setFormData(formData => ({ ...(formData as any), [key]: newValue }));
	};

	setFormData = (reducer: (oldData: FormData) => FormData) => {
		const { onChange, validator } = this.props;
		this.setState(
			state => {
				const formData = reducer(state.formData);
				const validation = validator(formData, "change", state.validation, state.hasSubmittedOnce);
				const isPristine = false;
				return { ...state, formData, isPristine, validation };
			},
			() => {
				if (onChange) {
					// eslint-disable-next-line @eslint-react/no-access-state-in-setstate
					onChange(this.state.formData, this.state.validation);
				}
			},
		);
	};

	// Clears the submission state by setting hasSubmittedOnce to false and thus stops validating until the next submit attempt
	clearSubmissionState = () => {
		const { validator } = this.props;
		this.setState(state => {
			const validation = validator(state.formData, "change", state.validation, false);
			return { ...state, hasSubmittedOnce: false, validation };
		});
	};

	onSubmit = (event: FormEvent<HTMLFormElement>) => {
		const { formData } = this.state;
		const { validator, onSubmit, stopSubmitPropagation = false } = this.props;

		event.preventDefault();

		// TODO: try another approach by stop even bubbling in the modal instead
		// https://dev.azure.com/bokiodev/Voder/_workitems/edit/29236
		if (stopSubmitPropagation && typeof event.stopPropagation === "function") {
			// prevent any outer forms receiving submit event
			event.stopPropagation();
		}

		const validation = validator(formData, "submit", this.state.validation, true);

		this.setState(state => ({ ...state, validation, hasSubmittedOnce: true }));
		if (validation.isValid) {
			onSubmit(formData, validation);
		}
	};
}
